import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import * as yup from 'yup';

import { Box, Button, styled } from '@mui/material';
import { TFunction } from 'i18next';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useSnackbar } from '@cnx-saas/snackbar';
import { RhfTextField } from '@cnx-saas/common-fe';
import { usePasswordResetMutation } from '../../../api/tenant/mutations/send-reset-password';
import { Separator } from '../../shared';
import { SplitView } from '../../split-view';

type ForgotPasswordInput = {
  email: string;
};

export function ForgotPassword() {
  const { t } = useTranslation();

  const { control, handleSubmit } = useForm<ForgotPasswordInput>({
    resolver: yupResolver(registrationFormSchema(t)),
  });
  const { addAlert } = useSnackbar();

  const [sendPasswordResetEmail] = usePasswordResetMutation();

  const onSubmit = async (input: ForgotPasswordInput) => {
    const response = await sendPasswordResetEmail({
      variables: {
        details: {
          email: input.email,
        },
      },
    });
    if (response.errors) {
      addAlert({
        type: 'error',
        children: response.errors.toString(),
      });
    } else {
      addAlert({
        type: 'success',
        children: t('registration.edit-user-dialog.success', {
          defaultValue: 'Email has been sent',
        }),
      });
    }
  };

  return (
    <SplitView title={t('userManagement.forgotPassword.title')}>
      <p>{t('login.resetPassword.body')}</p>
      <Root onSubmit={handleSubmit(onSubmit)}>
        <Box display={'flex'} flexDirection={'column'}>
          <RhfTextField
            control={control}
            name="email"
            label={t('userManagement.forgotPassword.email', {
              defaultValue: 'Email*',
            })}
          />
          <Button color="primary" type="submit">
            {t('login.resetPassword.button')}
          </Button>
        </Box>
      </Root>
      <Separator />
      <p>
        {t('login.resetPassword.rememberPassword')}{' '}
        <Link to="/login">{t('login.resetPassword.backToLogin')}</Link>
      </p>
    </SplitView>
  );
}

export const Root = styled('form')({
  width: '100%',
});

export const registrationFormSchema = (t: TFunction) => {
  const fieldRequiredMessage = t(
    'registration.form.validation-error.required',
    {
      defaultValue: 'This field is required.',
    },
  );

  return yup.object().shape({
    email: yup.string().required(fieldRequiredMessage),
  });
};
